import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Download, Search, FileText, X } from 'lucide-react';
import { format } from 'date-fns';
import { getDateFnsLocale } from '../../utils/dateLocale';
import { useTranslation } from 'react-i18next';
import adminApi from '../../services/adminApi';
import { useAuth } from '../../contexts/AuthContext';
import useAdminStore from '../../store/adminStore';
import { getApiErrorMessage } from '../../utils/apiErrorMessage';

const inputClass =
  'px-4 py-2 bg-white dark:bg-[#161f31] border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-white [color-scheme:light] dark:[color-scheme:dark] focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50 text-sm';

const LogTable = () => {
  const { t, i18n } = useTranslation('admin');
  const dateLocale = getDateFnsLocale(i18n.language);
  const { authToken } = useAuth();
  const { logs, filters, setLogs, setFilters, setLoading, setError } = useAdminStore();
  const [search, setSearch] = useState('');

  const loadLogs = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await adminApi.getLogs({
        token: authToken,
        filters: {
          type: filters.logType !== 'all' ? filters.logType : undefined,
          startDate: filters.logStartDate,
          endDate: filters.logEndDate,
        },
      });
      if (result.success) {
        setLogs(result.logs || []);
      } else {
        setError(result.error || t('logTable.loadFailedFallback'));
      }
    } catch (error) {
      setError(getApiErrorMessage(error, { fallback: t('logTable.genericErrorFallback') }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, [authToken, filters.logType, filters.logStartDate, filters.logEndDate]);

  const handleExport = async () => {
    setLoading(true);
    try {
      const result = await adminApi.exportLogs({
        token: authToken,
        filters: {
          type: filters.logType !== 'all' ? filters.logType : undefined,
          startDate: filters.logStartDate,
          endDate: filters.logEndDate,
        },
      });
      if (result.success && result.csv) {
        const blob = new Blob([result.csv], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `logs_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      setError(getApiErrorMessage(error, { fallback: t('logTable.exportFailedFallback') }));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('logTable.unknown');
    try {
      return format(new Date(dateString), 'dd MMMM yyyy, HH:mm:ss', { locale: dateLocale });
    } catch {
      return t('logTable.invalidDate');
    }
  };

  const getLogTypeBadge = (type) => {
    const styles = {
      login: 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
      logout: 'bg-gray-100 dark:bg-[var(--color-bg-elevated)] text-gray-700 dark:text-[var(--color-text-secondary)]',
      note_create: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      note_delete: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
      impersonate: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400',
      update_user: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
      delete_user: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400',
      create_user: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
      update_role: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
      admin_action: 'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
    };
    const labels = {
      login: t('logTable.logTypes.login'),
      logout: t('logTable.logTypes.logout'),
      note_create: t('logTable.logTypes.note_create'),
      note_delete: t('logTable.logTypes.note_delete'),
      impersonate: t('logTable.logTypes.impersonate'),
      update_user: t('logTable.logTypes.update_user'),
      delete_user: t('logTable.logTypes.delete_user'),
      create_user: t('logTable.logTypes.create_user'),
      update_role: t('logTable.logTypes.update_role'),
      admin_action: t('logTable.logTypes.admin_action'),
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[type] || styles.admin_action}`}>
        {labels[type] || type}
      </span>
    );
  };

  const filteredLogs = search.trim()
    ? logs.filter((log) => {
        const q = search.toLowerCase();
        return (
          (log.userName || '').toLowerCase().includes(q) ||
          (log.action || '').toLowerCase().includes(q)
        );
      })
    : logs;

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-2xl font-bold text-gray-900 dark:text-white">{t('logTable.title')}</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            {filteredLogs.length !== logs.length
              ? t('logTable.filteredCount', { filtered: filteredLogs.length, total: logs.length })
              : t('logTable.totalCount', { count: logs.length })}
          </p>
        </div>
        <motion.button
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          onClick={handleExport}
          className="flex items-center space-x-2 px-4 py-2 bg-[#2563eb] hover:bg-[#3d8ef0] text-white rounded-lg font-medium transition-colors"
        >
          <Download className="w-5 h-5" />
          <span>{t('logTable.csvExport')}</span>
        </motion.button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Arama */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('logTable.searchPlaceholder')}
            className="w-full pl-9 pr-8 py-2 bg-white dark:bg-[#161f31] border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#3d8ef0]/50 text-sm"
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* Tip filtresi */}
        <select
          value={filters.logType}
          onChange={(e) => setFilters({ logType: e.target.value })}
          className={inputClass}
        >
          <option value="all">{t('logTable.allTypes')}</option>
          <option value="login">{t('logTable.logTypes.login')}</option>
          <option value="logout">{t('logTable.logTypes.logout')}</option>
          <option value="note_create">{t('logTable.logTypes.note_create')}</option>
          <option value="note_delete">{t('logTable.logTypes.note_delete')}</option>
          <option value="impersonate">{t('logTable.logTypes.impersonate')}</option>
          <option value="create_user">{t('logTable.logTypes.create_user')}</option>
          <option value="update_user">{t('logTable.logTypes.update_user')}</option>
          <option value="delete_user">{t('logTable.logTypes.delete_user')}</option>
          <option value="update_role">{t('logTable.logTypes.update_role')}</option>
          <option value="admin_action">{t('logTable.logTypes.admin_action')}</option>
        </select>

        {/* Tarih aralığı */}
        <div className="flex items-center gap-2">
          <input
            type="date"
            value={filters.logStartDate || ''}
            onChange={(e) => setFilters({ logStartDate: e.target.value || null })}
            className={inputClass}
          />
          <span className="text-gray-400 text-sm">—</span>
          <input
            type="date"
            value={filters.logEndDate || ''}
            onChange={(e) => setFilters({ logEndDate: e.target.value || null })}
            className={inputClass}
          />
        </div>

        {/* Temizle */}
        {(filters.logStartDate || filters.logEndDate || filters.logType !== 'all' || search) && (
          <button
            onClick={() => {
              setFilters({ logType: 'all', logStartDate: null, logEndDate: null });
              setSearch('');
            }}
            className="px-3 py-2 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 border border-gray-200 dark:border-white/10 rounded-lg transition-colors"
          >
            {t('logTable.clear')}
          </button>
        )}
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-[var(--color-bg-elevated)] rounded-xl shadow-sm border border-gray-200 dark:border-white/10 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-[#161f31]/50 border-b border-gray-200 dark:border-white/10">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('logTable.columns.type')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('logTable.columns.user')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('logTable.columns.action')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('logTable.columns.date')}</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">{t('logTable.columns.details')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-white/[0.07]">
              {filteredLogs.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-12 text-center text-gray-500 dark:text-gray-400">
                    <FileText className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>{search ? t('logTable.noSearchResults') : t('logTable.noRecords')}</p>
                  </td>
                </tr>
              ) : (
                filteredLogs.map((log) => (
                  <motion.tr
                    key={log.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="hover:bg-gray-50 dark:hover:bg-[#1e2d47]/30 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">{getLogTypeBadge(log.type)}</td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">{log.userName || t('logTable.unknown')}</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">{log.userId?.substring(0, 8)}...</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900 dark:text-white">{log.action}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900 dark:text-white">{formatDate(log.timestamp)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {log.details ? JSON.stringify(log.details) : '-'}
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default LogTable;


